/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.comp

import io.peekandpoke.klang.audio_bridge.analyzer.AnalyzerBuffer
import io.peekandpoke.klang.audio_bridge.analyzer.SPECTRUM_FFT_SIZE
import org.khronos.webgl.get
import kotlin.math.exp
import kotlin.math.ln
import kotlin.math.log10
import kotlin.math.log2
import kotlin.math.pow
import kotlin.math.sqrt

/**
 * Converts raw linear-spaced FFT data into perceptually balanced spectrum buckets.
 *
 * Applies three corrections that professional spectrum analyzers use:
 * 1. Logarithmic frequency binning — each bucket covers an equal musical interval
 * 2. RMS aggregation — proper energy measurement when combining multiple FFT bins
 * 3. Slope compensation (+3 dB/octave) — counteracts the natural 1/f spectral rolloff
 *
 * The floor and ceiling were tuned at 1024 bins. A bin of a larger FFT is narrower and holds less of the energy, so
 * the RMS over a column's bins falls 3 dB per doubling of [fftBinCount]; [resolutionDb] adds that back, and broadband
 * material (noise, a dense mix) reads the same at any FFT size. Two places where it does not: a lone low tone, in a
 * column narrower than the window's main lobe, reads up to about 4 dB hotter at 4096 bins than at 1024; and the
 * silence gate looks at the uncorrected level, so content just above the floor at 1024 bins (within 6 dB of it, at
 * 4096) stays dark.
 *
 * The result is a display where typical music looks balanced across the frequency range,
 * rather than showing the misleading bass-heavy downward slope of raw FFT data.
 */
class SpectrumBinning(
    val numBuckets: Int,
    val fftBinCount: Int = SPECTRUM_FFT_SIZE / 2,
    val sampleRate: Double = 48000.0,
    val minFreq: Double = 30.0,
    val maxFreq: Double = 20000.0,
    /** Slope compensation in dB per octave. 3.0 flattens typical pink-noise-like music. */
    val slopeDbPerOctave: Double = 3.0,
    /** Reference frequency where slope compensation is 0 dB. */
    val referenceFreq: Double = 1000.0,
) {
    /** Precomputed start/end FFT bin indices for each bucket (log-spaced). */
    private val bucketRanges: Array<IntRange>

    /** Precomputed slope compensation in dB for each bucket's center frequency. */
    private val slopeCompensationDb: DoubleArray

    /** The level lost to narrower bins against the 1024 the floor and ceiling were tuned at. */
    private val resolutionDb = 10.0 * log10(fftBinCount / 1024.0)

    init {
        val fftBinWidth = sampleRate / (fftBinCount * 2) // Hz per bin
        val logMin = ln(minFreq)
        val logMax = ln(maxFreq)

        bucketRanges = Array(numBuckets) { i ->
            val freqLow = frequencyRange(i).start
            val freqHigh = frequencyRange(i).endInclusive

            val binLow = (freqLow / fftBinWidth).toInt().coerceIn(0, fftBinCount - 1)
            val binHigh = (freqHigh / fftBinWidth).toInt().coerceIn(binLow, fftBinCount - 1)

            binLow..binHigh
        }

        slopeCompensationDb = DoubleArray(numBuckets) { i ->
            val freqCenter = exp(logMin + (logMax - logMin) * (i + 0.5) / numBuckets)
            slopeDbPerOctave * log2(freqCenter / referenceFreq)
        }
    }

    /** The frequencies [bucket] covers, in Hz, low to high: its column spans them on a log scale. */
    fun frequencyRange(bucket: Int): ClosedFloatingPointRange<Double> {
        val logMin = ln(minFreq)
        val logMax = ln(maxFreq)

        return exp(logMin + (logMax - logMin) * bucket / numBuckets)..exp(logMin + (logMax - logMin) * (bucket + 1) / numBuckets)
    }

    /**
     * Processes raw FFT dB data into [numBuckets] perceptually balanced values.
     *
     * @param fftBuffer Raw FFT data in dB (as returned by AnalyserNode.getFloatFrequencyData)
     * @param out Array of size [numBuckets] that receives normalized values in 0.0..1.0
     * @param dbFloor dB value that maps to 0.0 (silence)
     * @param dbCeil dB value that maps to 1.0 (maximum)
     */
    fun process(fftBuffer: AnalyzerBuffer, out: DoubleArray, dbFloor: Double = -99.0, dbCeil: Double = -10.0) {
        val range = dbCeil - dbFloor

        for (i in 0 until numBuckets) {
            val binRange = bucketRanges[i]
            val binCount = (binRange.last - binRange.first + 1).coerceAtLeast(1)

            // RMS aggregation: convert dB → linear, sum squares, average, back to dB
            var sumSquares = 0.0
            for (bin in binRange) {
                val dbValue = fftBuffer[bin].toDouble()
                val linear = 10.0.pow(dbValue / 20.0)
                sumSquares += linear * linear
            }
            val rmsDb = 20.0 * log10(sqrt(sumSquares / binCount).coerceAtLeast(1e-10))

            // Apply slope and resolution compensation, but not to silence: otherwise high-freq
            // buckets get boosted above the floor and show phantom bars.
            val compensatedDb = if (rmsDb < dbFloor) dbFloor else rmsDb + slopeCompensationDb[i] + resolutionDb

            // Normalize to 0.0..1.0
            out[i] = ((compensatedDb - dbFloor) / range).coerceIn(0.0, 1.0)
        }
    }
}
